import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
  ReactNode,
  useMemo,
} from "react";
import {
  useCheckAuth,
  useGetIdentity,
  useNotify,
  useDataProvider,
  useTranslate,
  usePermissions,
  useRefresh,
} from "react-admin";
import { ExpireTermsStatus } from "../constants/define";
import { TermsOfServiceDialog } from "../components/TermsOfServiceDialog";

interface TermsAgreementContextState {
  isLoading: boolean;
  status: {
    terms: ExpireTermsStatus | null;
    contract: ExpireTermsStatus | null;
  };
  showDialog: (type: "terms" | "contract" | "contractPeriod") => void;
}

const TermsAgreementContext = createContext<
  TermsAgreementContextState | undefined
>(undefined);

export const TermsAgreementProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const [activeDialog, setActiveDialog] = useState<
    "none" | "terms" | "contract" | "contractPeriod"
  >("none");

  const [termsStatus, setTermsStatus] = useState<ExpireTermsStatus | null>(
    null,
  );
  const [contractStatus, setContractStatus] =
    useState<ExpireTermsStatus | null>(null);
  const [contractPeriodStatus, setContractPeriodStatus] =
    useState<ExpireTermsStatus | null>(null);

  const [termsDialogInfo, setTermsDialogInfo] = useState<{
    text: string;
    deadline: Date | null;
    pdfLink: string | null;
  } | null>(null);
  const [contractDialogInfo, setContractDialogInfo] = useState<{
    text: string;
    deadline: Date | null;
    pdfLink: string | null;
  } | null>(null);
  const [contractDialogPeriodInfo, setContractDialogPeriodInfo] = useState<{
    text: string;
    deadline: Date | null;
    pdfLink: string | null;
  } | null>(null);

  const [isLoading, setIsLoading] = useState(true);

  const checkAuth = useCheckAuth();
  const { identity, isLoading: identityLoading } = useGetIdentity();
  const notify = useNotify();
  const dataProvider = useDataProvider();
  const refresh = useRefresh();

  const hasPerformedCheck = useRef(false);
  const isAutoSequence = useRef(true);

  useEffect(() => {
    const performTermsCheck = async () => {
      if (identity && !identityLoading && !hasPerformedCheck.current) {
        hasPerformedCheck.current = true;
        isAutoSequence.current = true;

        setIsLoading(true);
        try {
          const data = await dataProvider.checkExpireTerms();
          setTermsStatus(data.expireTermsStatus);
          setContractStatus(data.expireContractStatus);
          setContractPeriodStatus(data.expireContractPeriodStatus);

          setTermsDialogInfo({
            text: data.terms,
            deadline: data.termsAgreementDeadlineAt
              ? new Date(data.termsAgreementDeadlineAt)
              : null,
            pdfLink: data.termsLinkPDF,
          });
          setContractDialogInfo({
            text: data.contract,
            deadline: data.contractAgreementDeadlineAt
              ? new Date(data.contractAgreementDeadlineAt)
              : null,
            pdfLink: data.contractLinkPDF,
          });
          setContractDialogPeriodInfo({
            text: data.contract,
            deadline: data.contractPeriodDeadlineAt
              ? new Date(data.contractPeriodDeadlineAt)
              : null,
            pdfLink: data.contractLinkPDF,
          });

          if (
            data.expireTermsStatus === ExpireTermsStatus.Warning ||
            data.expireTermsStatus === ExpireTermsStatus.Expired
          ) {
            setActiveDialog("terms");
          } else if (
            data.expireContractStatus === ExpireTermsStatus.Warning ||
            data.expireContractStatus === ExpireTermsStatus.Expired
          ) {
            setActiveDialog("contract");
          } else if (
            data.expireContractPeriodStatus === ExpireTermsStatus.Warning ||
            data.expireContractPeriodStatus === ExpireTermsStatus.Expired
          ) {
            setActiveDialog("contractPeriod");
          }
        } catch (error: any) {
          setTermsStatus(ExpireTermsStatus.None);
          setContractStatus(ExpireTermsStatus.None);
          setContractPeriodStatus(ExpireTermsStatus.None);
        } finally {
          setIsLoading(false);
        }
      } else if (!identityLoading) {
        setIsLoading(false);
      }
    };

    checkAuth({}, false)
      .then(performTermsCheck)
      .catch(() => {
        hasPerformedCheck.current = false;
        isAutoSequence.current = true;
        setIsLoading(false);
      });
  }, [checkAuth, identity, identityLoading, dataProvider, notify]);

  const onCloseDialog = useCallback(
    async (agreed: boolean) => {
      // 同意APIを呼び出す場合は、どのダイアログに対する同意かを`activeDialog`で判断
      if (agreed) {
        if (activeDialog === "terms") {
          await dataProvider.agreeTerms();
          setTermsStatus(ExpireTermsStatus.None);
        }
        if (activeDialog === "contract") {
          await dataProvider.agreeContract();
          setContractStatus(ExpireTermsStatus.None);
        }
        if (activeDialog === "contractPeriod") {
          await dataProvider.extendContractPeriod();
          setContractStatus(ExpireTermsStatus.None);
          setContractPeriodStatus(ExpireTermsStatus.None);

          // 契約期間の延長が完了した場合、トーストを表示
          notify("契約期間の延長が完了しました。", { type: "success" });

          // 延長後は表示中のレコードを再取得して「期限切れ」表示を解消する
          refresh();
        }
      }

      // 自動シーケンスモードが有効で、かつ利用規約ダイアログを閉じ、
      // さらに契約内容ダイアログの表示が必要な場合にのみ、次のダイアログへ遷移する
      if (
        isAutoSequence.current &&
        activeDialog === "terms" &&
        (contractStatus === ExpireTermsStatus.Warning ||
          contractStatus === ExpireTermsStatus.Expired)
      ) {
        setActiveDialog("contract");
      } else if (
        isAutoSequence.current &&
        activeDialog === "contract" &&
        (contractPeriodStatus === ExpireTermsStatus.Warning ||
          contractPeriodStatus === ExpireTermsStatus.Expired)
      ) {
        setActiveDialog("contractPeriod");
      } else {
        // 手動表示の場合や、シーケンスの最後のダイアログを閉じた場合は、すべて非表示にする
        setActiveDialog("none");
      }
    },
    [activeDialog, contractStatus, contractPeriodStatus, notify, refresh],
  );

  // PDFダウンロードアクション
  const onDownloadPdf = useCallback(
    async (type: "terms" | "contract") => {
      const link =
        type === "terms"
          ? termsDialogInfo?.pdfLink
          : contractDialogInfo?.pdfLink;
      return dataProvider.download(link || null);
    },
    [dataProvider, termsDialogInfo, contractDialogInfo],
  );

  const showDialog = useCallback(
    (type: "terms" | "contract" | "contractPeriod") => {
      isAutoSequence.current = false;
      setActiveDialog(type);
    },
    [],
  );

  const value = useMemo(
    () => ({
      isLoading: identityLoading || isLoading,
      status: {
        terms: termsStatus,
        contract: contractStatus,
        contractPeriod: contractPeriodStatus,
      },
      showDialog,
    }),
    [
      identityLoading,
      isLoading,
      termsStatus,
      contractStatus,
      contractPeriodStatus,
      showDialog,
    ],
  );

  return (
    <TermsAgreementContext.Provider value={value as any}>
      {children}

      {/* 利用規約ダイアログ */}
      {termsDialogInfo && (
        <TermsOfServiceDialog
          open={activeDialog === "terms"}
          onClose={onCloseDialog}
          prefix="terms"
          termsText={termsDialogInfo.text}
          agreementDeadline={termsDialogInfo.deadline}
          expireTermStatus={termsStatus!}
          onDownloadPdf={() => onDownloadPdf("terms")}
        />
      )}

      {/* 契約内容ダイアログ */}
      {contractDialogInfo && (
        <TermsOfServiceDialog
          open={activeDialog === "contract"}
          onClose={onCloseDialog}
          prefix="contract"
          termsText={contractDialogInfo.text}
          agreementDeadline={contractDialogInfo.deadline}
          expireTermStatus={contractStatus!}
          onDownloadPdf={() => onDownloadPdf("contract")}
        />
      )}

      {/* 契約更新ダイアログ */}
      {contractDialogPeriodInfo && (
        <TermsOfServiceDialog
          open={activeDialog === "contractPeriod"}
          onClose={onCloseDialog}
          prefix="contractPeriod"
          termsText={contractDialogPeriodInfo.text}
          agreementDeadline={contractDialogPeriodInfo.deadline}
          expireTermStatus={contractPeriodStatus!}
          onDownloadPdf={() => onDownloadPdf("contract")}
        />
      )}
    </TermsAgreementContext.Provider>
  );
};

// カスタムフック
export const useTermsAgreement = (): TermsAgreementContextState => {
  const context = useContext(TermsAgreementContext);
  if (context === undefined) {
    throw new Error(
      "useTermsAgreement must be used within a TermsAgreementProvider",
    );
  }
  return context;
};
